/**
 * Internal Entries View — Hourglass internal-staff view of attorney time entries.
 *
 * This is a deliberate copy of the attorney `ReportingView` ("Team" page),
 * adapted for Hourglass internal staff. It lets an internal employee select
 * any attorney at the firm, view their tracked/exported entries in a calendar
 * or table, and add / edit / delete / duplicate / combine / export entries on
 * that attorney's behalf.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY A COPY (not a shared component):
 * ReportingView and this view are expected to diverge meaningfully over time
 * (internal staff will get cross-firm tooling, bulk operations, audit views,
 * etc. that managers should never see). Extracting a shared base now would
 * force a base component that bends to support three different shapes
 * (admin entries, manager reporting, internal). We accept the duplication and
 * the cost of maintaining two files.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * BACKEND DEPENDENCY — "Pattern A" delegation bypass (see PR description):
 * This view reuses the attorney reporting data path:
 *   - reads come from the `reporting_entries` cache → GET /team/entries
 *   - writes go through `useReportingApi`, which injects the delegation
 *     headers `x-act-as-prof-id` / `x-acting-for`.
 *
 * Those endpoints scope results/permissions by the `reports_to` table — i.e.
 * they only allow acting on the caller's direct reports. Internal staff are
 * NOT managers-of-record, so without a backend change they'd see an empty
 * dropdown's worth of data and get 403s on writes.
 *
 * The backend change ("Pattern A") adds an internal-staff bypass: when the
 * caller is a Hourglass internal user (`@hourglass.law`), GET /team/entries
 * returns all `licensed = 1` attorneys at the firm, and the delegation
 * middleware honors `x-act-as-prof-id` for any licensed attorney at the firm
 * rather than only direct reports.
 *
 * Until that backend bypass ships, this view will render the attorney
 * dropdown (sourced from `useProfessionals`, NOT `reports_to`) but selecting
 * an attorney the caller doesn't manage will show no entries and 403 on
 * write. That is expected and intentional for the interim — the dropdown is
 * intentionally decoupled from the backend's authorization scope so the UI is
 * ready the moment the bypass lands.
 */

import { useState, useMemo, useCallback, useEffect } from "react";
import { ChevronsUpDown, Check, User, List, Calendar } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/shared/components/ui/command";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { useAuth } from "@workos-inc/authkit-react";
import { HeaderBar } from "@/features/attorney/components/HeaderBar";
import { useDateParams } from "@/shared/hooks/useDateParams";
import { useApplicationFilter } from "@/features/attorney/hooks/useApplicationFilter";
import {
	useReportsCache,
	useMattersCache,
	useTasksCache,
	useActivitiesCache,
	useAbbreviationsCache,
	type ReportingEntry,
} from "@/shared/cache";
import type { ReportingEntryStatus } from "@/shared/cache/slices/reportingEntries";
import { StatusFilterChips } from "../components/StatusFilterChips";
import {
	useInternalEntries,
	useInvalidateInternalEntries,
} from "../hooks/useInternalEntries";
import { useReportingApi } from "@/features/attorney/hooks/useReportingApi";
import { ApiError } from "@/v2/hooks/actions/types";
import { useProfessionals } from "@/features/admin/hooks/useAdminData";
import { type Professional } from "@/features/admin/utils/adminHelpers";
import { toCreateBody } from "@/v2/hooks/actions/add";
import { toUpdateBody } from "@/v2/hooks/actions/edit";
import { formatEntryId, parseEntryId } from "@/v2/hooks/actions/entryId";
import { useExportEntries } from "@/features/attorney/hooks/useExportEntries";
import { usePersistedState } from "@/features/attorney/hooks/usePersistedState";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { toast } from "sonner";
import { TOAST_DURATION } from "@/shared/utils/toast";
import { logger } from "@/shared/utils/logger";
import { type SelectedRowData } from "@/features/attorney/components/v2/EditEntryBody";
import { InternalEditEntry } from "../components/InternalEditEntry";
import { MergeDialog } from "@/v2/components/MergeDialog";
import { GroupDialog } from "@/v2/components/GroupDialog";
import {
	CollapseDialog,
	type CollapseDialogSubmit,
} from "@/v2/components/CollapseDialog";
import type { ActionFormSubmit } from "@/v2/components/action-dialog/ActionForm";
import type { Entry as SharedEntry } from "@/shared/types/entries";
import { InternalAddEntryDialog } from "../components/InternalAddEntryDialog";
import {
	InternalEntriesTable,
	type InternalEntry as Entry,
	type SortingMode,
	type SortDirection,
} from "../components/InternalEntriesTable";
import calendarStyles from "@/features/attorney/views/CalendarView/CalendarView.module.css";
import navArrowLeftIcon from "@/assets/icons/navArrowLeft.svg";
import navArrowLeftWhiteIcon from "@/assets/icons/navArrowLeftWhite.svg";
import navArrowRightIcon from "@/assets/icons/navArrowRight.svg";
import navArrowRightWhiteIcon from "@/assets/icons/navArrowRightWhite.svg";

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

type ViewMode = "list" | "calendar";

const ALL_STATUSES: ReportingEntryStatus[] = ["tracked", "exported", "deleted"];

function parseStatuses(raw: string | null): Set<ReportingEntryStatus> {
	const parsed = new Set<ReportingEntryStatus>(
		raw
			?.split(",")
			.filter((s): s is ReportingEntryStatus =>
				ALL_STATUSES.includes(s as ReportingEntryStatus),
			) ?? [],
	);
	return parsed.size > 0 ? parsed : new Set(["tracked"]);
}

function formatDateForApi(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

/** Format ISO date to MM/dd/yyyy (same as EntriesContext.transformData) */
function formatDateForEntry(isoDate: string): string {
	const datePart = isoDate.split("T")[0];
	const [year, month, day] = datePart.split("-").map(Number);
	const m = String(month).padStart(2, "0");
	const d = String(day).padStart(2, "0");
	return `${m}/${d}/${year}`;
}

function toEntry(tx: ReportingEntry, status: ReportingEntryStatus): Entry {
	return {
		id: tx.id,
		date: formatDateForEntry(tx.date),
		matter: tx.matter_number || "",
		matter_id: tx.matter_id || undefined,
		matter_name: tx.matter_description || undefined,
		description: tx.description || "",
		task: tx.task || "",
		activity: tx.activity || undefined,
		document_name: tx.document_name || undefined,
		duration: tx.units,
		start_time: tx.start_time ?? undefined,
		end_time: tx.end_time || undefined,
		application: tx.application || undefined,
		status,
	};
}

// ─────────────────────────────────────────────
// Attorney Selector (rendered as top-bar leftContent)
//
// Sourced from `useProfessionals` (all licensed attorneys at the firm),
// NOT `reports_to`. See the Pattern A note in the file header.
// ─────────────────────────────────────────────

function AttorneySelector({
	professionals,
	selectedProfId,
	onSelect,
}: {
	professionals: Professional[];
	selectedProfId: string | null;
	onSelect: (profId: string) => void;
}) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");

	const selectedMember = professionals.find(
		(p) => p.prof_id === selectedProfId,
	);

	const filtered = useMemo(() => {
		if (!search) return professionals;
		const term = search.toLowerCase();
		return professionals.filter(
			(p) =>
				p.full_name.toLowerCase().includes(term) ||
				(p.email && p.email.toLowerCase().includes(term)),
		);
	}, [professionals, search]);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-muted transition-colors text-left">
					<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-foreground text-sm font-medium">
						{selectedMember ? (
							selectedMember.full_name
								.split(/[\s,]+/)
								.filter(Boolean)
								.slice(0, 2)
								.map((n) => n[0])
								.join("")
								.toUpperCase()
						) : (
							<User className="h-4 w-4 text-muted-foreground" />
						)}
					</div>
					<div className="flex flex-col min-w-0">
						<span className="text-sm font-medium text-foreground truncate">
							{selectedMember ? selectedMember.full_name : "Select attorney"}
						</span>
						{!selectedMember && (
							<span className="text-xs text-muted-foreground">
								Click to choose
							</span>
						)}
					</div>
					<ChevronsUpDown className="ml-1 h-4 w-4 text-muted-foreground shrink-0" />
				</button>
			</PopoverTrigger>
			<PopoverContent className="w-[300px] p-0" align="start">
				<Command>
					<CommandInput
						placeholder="Search by name or email..."
						value={search}
						onValueChange={setSearch}
					/>
					<CommandList>
						<CommandEmpty>No attorneys found.</CommandEmpty>
						<CommandGroup>
							{filtered.map((member) => (
								<CommandItem
									key={member.prof_id}
									value={`${member.full_name} ${member.email ?? ""}`}
									onSelect={() => {
										onSelect(member.prof_id);
										setOpen(false);
										setSearch("");
									}}
								>
									<Check
										className={cn(
											"mr-2 h-4 w-4 shrink-0",
											selectedProfId === member.prof_id
												? "opacity-100"
												: "opacity-0",
										)}
									/>
									<div className="flex flex-col min-w-0">
										<span className="truncate">{member.full_name}</span>
										<span className="text-xs text-muted-foreground truncate">
											{member.email}
										</span>
									</div>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────

export default function EntriesView() {
	const { darkMode } = useDarkMode();
	const { user } = useAuth();
	// Source 1: firm-wide professionals (excludes @hourglass.law staff because
	// the backend's /admin/professionals endpoint hardcodes that filter and
	// ignores any param to disable it).
	const { professionals, loading: profLoading } = useProfessionals({
		activeOnly: true,
	});
	// Source 2: caller's `reports_to.manages` list — this endpoint does NOT
	// filter internal staff, so it's how Hourglass-internal users (TJ, Test
	// Account, etc.) end up visible in the picker. Anyone we want to act on
	// behalf of can be added to reports_to and will appear here.
	const { reports } = useReportsCache();

	// Merge both sources, dedupe by prof_id. Result: every firm attorney plus
	// any internal user the caller manages. Strictly scoped to this view.
	const allProfessionals = useMemo<Professional[]>(() => {
		const byId = new Map<string, Professional>();
		for (const p of professionals) byId.set(p.prof_id, p);
		for (const r of reports.manages) {
			// Sandbox shadow profs ride in via reports_to; backend should drop them
			// from /user/reports — until then, filter by the [sandbox] name prefix.
			if (r.full_name?.startsWith("[sandbox]")) continue;
			if (!byId.has(r.prof_id)) {
				byId.set(r.prof_id, {
					prof_id: r.prof_id,
					full_name: r.full_name,
					email: r.email,
				});
			}
		}
		return Array.from(byId.values()).sort((a, b) =>
			a.full_name.localeCompare(b.full_name),
		);
	}, [professionals, reports.manages]);

	// URL-driven state
	const [searchParams, setSearchParams] = useSearchParams();
	const {
		date: selectedDate,
		dateRange,
		setDate: setSelectedDate,
		setDateRange,
	} = useDateParams();

	// ?show=tracked,exported,deleted (default: tracked only)
	// ?view=list|calendar             (default: list)
	const visibleStatuses = useMemo(
		() => parseStatuses(searchParams.get("show")),
		[searchParams],
	);

	const viewMode: ViewMode =
		searchParams.get("view") === "calendar" ? "calendar" : "list";

	const toggleStatus = useCallback(
		(status: ReportingEntryStatus) => {
			const next = new URLSearchParams(window.location.search);
			const current = parseStatuses(next.get("show"));
			if (current.has(status)) current.delete(status);
			else current.add(status);
			if (current.size === 0) current.add("tracked");
			const value = ALL_STATUSES.filter((s) => current.has(s)).join(",");
			if (value === "tracked") next.delete("show");
			else next.set("show", value);
			setSearchParams(next, { replace: true });
		},
		[setSearchParams],
	);

	const setViewMode = useCallback(
		(mode: ViewMode) => {
			const next = new URLSearchParams(window.location.search);
			if (mode === "list") next.delete("view");
			else next.set("view", mode);
			setSearchParams(next, { replace: true });
		},
		[setSearchParams],
	);

	const selectedDateStr = useMemo(
		() => formatDateForApi(selectedDate),
		[selectedDate],
	);

	const clientFilter = searchParams.get("client") || null;
	const matterFilter = searchParams.get("matter") || null;

	const setParams = useCallback(
		(updates: Record<string, string | null>) => {
			const next = new URLSearchParams(window.location.search);
			for (const [key, value] of Object.entries(updates)) {
				if (!value) next.delete(key);
				else next.set(key, value);
			}
			setSearchParams(next, { replace: true });
		},
		[setSearchParams],
	);

	const selectedProfId = searchParams.get("attorney") || null;
	const setSelectedProfId = useCallback(
		(profId: string | null) => {
			const next = new URLSearchParams(window.location.search);
			if (profId) next.set("attorney", profId);
			else next.delete("attorney");
			setSearchParams(next, { replace: true });
		},
		[setSearchParams],
	);

	// Persisted state (non-URL)
	const [sortingMode] = usePersistedState<SortingMode>(
		"internal:sortingMode",
		"time",
	);
	const [sortDirection] = usePersistedState<SortDirection>(
		"internal:sortDirection",
		"desc",
	);

	// Application filter
	const {
		isVisible: isApplicationVisible,
		toggleApplication,
		filterEntries,
		showAll: showAllApplications,
		hideAll: hideAllApplications,
	} = useApplicationFilter(user?.email || "");

	// Calendar state
	const [calendarMonthDate, setCalendarMonthDate] = useState<Date>(new Date());

	// Server-side range. Calendar mode fetches the full visible month so dailySums
	// covers every cell; list mode fetches the active date or range.
	const fetchRange = useMemo(() => {
		if (viewMode === "calendar") {
			const start = new Date(
				calendarMonthDate.getFullYear(),
				calendarMonthDate.getMonth(),
				1,
			);
			const end = new Date(
				calendarMonthDate.getFullYear(),
				calendarMonthDate.getMonth() + 1,
				0,
			);
			return { start, end };
		}
		if (dateRange.from && dateRange.to) {
			return { start: dateRange.from, end: dateRange.to };
		}
		return { start: selectedDate, end: selectedDate };
	}, [viewMode, calendarMonthDate, dateRange.from, dateRange.to, selectedDate]);

	const {
		tracked: cachedTracked,
		exported: cachedExported,
		deleted: cachedDeleted,
		isLoading: entriesLoading,
		isError: entriesError,
		error: entriesErrorMessage,
	} = useInternalEntries(
		selectedProfId,
		fetchRange.start,
		fetchRange.end,
		true,
	);
	const invalidateInternal = useInvalidateInternalEntries();
	const refresh = useCallback(
		() => invalidateInternal(selectedProfId),
		[invalidateInternal, selectedProfId],
	);

	// Reporting API (injects x-act-as-prof-id / x-acting-for headers)
	const { makeReportingRequest } = useReportingApi(selectedProfId);

	// Mutation tracking (visual disabled state on entries being modified)
	const [, setMutatingIds] = useState<Set<number>>(new Set());

	// Failed export tracking
	const [failedExportEntries, setFailedExportEntries] = useState<
		Map<number, string>
	>(new Map());

	// Export hook
	const { exportEntries, isExporting } = useExportEntries({
		makeApiRequest: makeReportingRequest,
		onComplete: ({ failedEntries }) => {
			refresh();
			if (failedEntries.length > 0) {
				setFailedExportEntries((prev) => {
					const next = new Map(prev);
					for (const f of failedEntries) next.set(f.id, f.error);
					return next;
				});
			}
		},
		progressToastId: "internal-export-progress",
	});

	// Lookup data for Add/Edit/Combine dialogs
	const { matters } = useMattersCache();
	const { tasks } = useTasksCache();
	const { activities } = useActivitiesCache();
	const { abbreviationProps } = useAbbreviationsCache();

	// Add entry dialog
	const [addDialogOpen, setAddDialogOpen] = useState(false);

	// Edit entry dialog
	const [showEditModal, setShowEditModal] = useState(false);
	const [selectedRowData, setSelectedRowData] =
		useState<SelectedRowData | null>(null);
	const [selectedExportError, setSelectedExportError] = useState<string | null>(
		null,
	);

	// Action dialogs — null when closed; entries[] when open.
	const [mergeEntries, setMergeEntries] = useState<SharedEntry[] | null>(null);
	const [mergeConflicts, setMergeConflicts] = useState<
		{ field: string; values: string[] }[] | undefined
	>(undefined);
	const [groupEntries, setGroupEntries] = useState<SharedEntry[] | null>(null);
	const [groupConflicts, setGroupConflicts] = useState<
		{ field: string; values: string[] }[] | undefined
	>(undefined);
	const [collapseEntries, setCollapseEntries] = useState<SharedEntry[] | null>(
		null,
	);

	// Selection state
	const [actionInProgress, setActionInProgress] = useState<
		"merge" | "group" | "collapse" | "delete" | "duplicate" | null
	>(null);

	const statusKey = ALL_STATUSES.filter((s) => visibleStatuses.has(s)).join(
		",",
	);
	useEffect(() => {
		setCheckedRows(new Set());
	}, [selectedProfId, viewMode, statusKey]);

	const sourceEntries = useMemo(() => {
		const parts: ReportingEntry[] = [];
		if (visibleStatuses.has("tracked")) parts.push(...cachedTracked);
		if (visibleStatuses.has("exported")) parts.push(...cachedExported);
		if (visibleStatuses.has("deleted")) parts.push(...cachedDeleted);
		return parts;
	}, [visibleStatuses, cachedTracked, cachedExported, cachedDeleted]);

	// ids are unique across the three buckets — one row never appears in two.
	const statusById = useMemo(() => {
		const m = new Map<number, ReportingEntryStatus>();
		for (const e of cachedTracked) m.set(e.id, "tracked");
		for (const e of cachedExported) m.set(e.id, "exported");
		for (const e of cachedDeleted) m.set(e.id, "deleted");
		return m;
	}, [cachedTracked, cachedExported, cachedDeleted]);

	const allProfEntriesForFilters = useMemo(
		() => [...cachedTracked, ...cachedExported, ...cachedDeleted],
		[cachedTracked, cachedExported, cachedDeleted],
	);

	const availableClients = useMemo(() => {
		const set = new Set<string>();
		for (const e of allProfEntriesForFilters) if (e.client) set.add(e.client);
		return Array.from(set).sort();
	}, [allProfEntriesForFilters]);

	const availableMatters = useMemo(() => {
		const map = new Map<
			string,
			{
				matterId: string;
				matterNumber: string;
				matterDescription: string;
				client: string;
			}
		>();
		for (const e of allProfEntriesForFilters) {
			if (e.matter_id && !map.has(e.matter_id)) {
				if (!clientFilter || e.client === clientFilter) {
					map.set(e.matter_id, {
						matterId: e.matter_id,
						matterNumber: e.matter_number || "",
						matterDescription: e.matter_description || "",
						client: e.client || "Unknown",
					});
				}
			}
		}
		return Array.from(map.values()).sort((a, b) =>
			a.matterNumber.localeCompare(b.matterNumber),
		);
	}, [allProfEntriesForFilters, clientFilter]);

	// Filter by date + client + matter (client-side).
	// Compare YYYY-MM-DD strings rather than Date objects: entry dates from
	// the API arrive as ISO strings parsed at UTC midnight, while nuqs's date
	// params land at LOCAL midnight — in any negative-UTC zone the Date-vs-
	// Date comparison wrongly excludes entries on the range start date.
	const dateFilteredEntries = useMemo(() => {
		let entries = sourceEntries;

		if (dateRange.from || dateRange.to) {
			const fromStr = dateRange.from ? formatDateForApi(dateRange.from) : null;
			const toStr = dateRange.to ? formatDateForApi(dateRange.to) : null;
			entries = entries.filter((e) => {
				const entryDateStr = e.date.split("T")[0];
				if (fromStr && entryDateStr < fromStr) return false;
				if (toStr && entryDateStr > toStr) return false;
				return true;
			});
		} else {
			entries = entries.filter((e) => e.date.split("T")[0] === selectedDateStr);
		}

		if (clientFilter)
			entries = entries.filter((e) => e.client === clientFilter);
		if (matterFilter)
			entries = entries.filter((e) => e.matter_id === matterFilter);

		return entries;
	}, [
		sourceEntries,
		selectedDateStr,
		dateRange.from,
		dateRange.to,
		clientFilter,
		matterFilter,
	]);

	// Sort and transform
	const tabFilteredEntries = useMemo(() => {
		const mult = sortDirection === "asc" ? 1 : -1;
		const toE = (tx: ReportingEntry) =>
			toEntry(tx, statusById.get(tx.id) ?? "tracked");
		if (sortingMode === "matter") {
			return [...dateFilteredEntries].map(toE).sort((a, b) => {
				const mA = a.matter_name || a.matter || "";
				const mB = b.matter_name || b.matter || "";
				if (!mA && mB) return 1;
				if (mA && !mB) return -1;
				if (!mA && !mB) return 0;
				return mult * mA.toLowerCase().localeCompare(mB.toLowerCase());
			});
		}
		return [...dateFilteredEntries]
			.sort(
				(a, b) => mult * (a.start_time || "").localeCompare(b.start_time || ""),
			)
			.map(toE);
	}, [dateFilteredEntries, sortingMode, sortDirection, statusById]);

	const filteredEntries = useMemo(
		() => filterEntries(tabFilteredEntries),
		[tabFilteredEntries, filterEntries],
	);

	const [checkedRows, setCheckedRows] = useState<Set<number>>(() => new Set());

	const selectionCount = checkedRows.size;
	const selectedIds = useMemo(() => Array.from(checkedRows), [checkedRows]);

	const selectionStatuses = useMemo(() => {
		const s = new Set<ReportingEntryStatus>();
		for (const id of checkedRows) {
			const st = statusById.get(id);
			if (st) s.add(st);
		}
		return s;
	}, [checkedRows, statusById]);

	const selectionAllTracked =
		selectionCount > 0 &&
		selectionStatuses.size === 1 &&
		selectionStatuses.has("tracked");
	const selectionHasDeleted = selectionStatuses.has("deleted");

	const handleExport = useCallback(async () => {
		if (selectedIds.length === 0) return;
		if (!selectionAllTracked) {
			toast.error("Only tracked entries can be exported", {
				duration: TOAST_DURATION.error,
			});
			return;
		}
		await exportEntries(selectedIds);
		setCheckedRows(new Set());
	}, [selectedIds, exportEntries, selectionAllTracked]);

	const handleDelete = useCallback(async () => {
		if (selectedIds.length === 0) return;
		if (selectionHasDeleted) {
			toast.error("Some entries are already deleted", {
				duration: TOAST_DURATION.error,
			});
			return;
		}
		setCheckedRows(new Set());
		setActionInProgress("delete");

		try {
			const resp = await makeReportingRequest({
				endpoint: "/records/delete",
				method: "POST",
				body: { ids: selectedIds },
			});
			if (resp.error) throw new Error(String(resp.error));
			toast.success(
				`Deleted ${selectedIds.length} ${selectedIds.length === 1 ? "entry" : "entries"}`,
			);
			refresh();
		} catch (e) {
			toast.error("Failed to delete entries", {
				duration: TOAST_DURATION.error,
				description: e instanceof Error ? e.message : undefined,
			});
		} finally {
			setActionInProgress(null);
		}
	}, [selectedIds, makeReportingRequest, refresh, selectionHasDeleted]);

	const handleDuplicate = useCallback(async () => {
		if (selectedIds.length === 0) return;
		if (selectionHasDeleted) {
			toast.error("Deleted entries can't be duplicated", {
				duration: TOAST_DURATION.error,
			});
			return;
		}

		// Mark source rows as mutating (no optimistic add — don't know new IDs)
		setMutatingIds((prev) => {
			const next = new Set(prev);
			selectedIds.forEach((id) => next.add(id));
			return next;
		});
		setActionInProgress("duplicate");

		try {
			const resp = await makeReportingRequest({
				endpoint: "/records/duplicate",
				method: "POST",
				body: { ids: selectedIds },
			});
			if (resp.error) throw new Error(String(resp.error));
			setCheckedRows(new Set());
			toast.success(
				`Duplicated ${selectedIds.length} ${selectedIds.length === 1 ? "entry" : "entries"}`,
			);
			refresh();
		} catch (e) {
			toast.error("Failed to duplicate entries", {
				duration: TOAST_DURATION.error,
				description: e instanceof Error ? e.message : undefined,
			});
		} finally {
			setMutatingIds((prev) => {
				const next = new Set(prev);
				selectedIds.forEach((id) => next.delete(id));
				return next;
			});
			setActionInProgress(null);
		}
	}, [selectedIds, makeReportingRequest, refresh, selectionHasDeleted]);

	const handleMerge = useCallback(() => {
		if (selectedIds.length < 2) {
			toast.error("Select at least 2 entries to merge", {
				duration: TOAST_DURATION.error,
			});
			return;
		}
		if (!selectionAllTracked) {
			toast.error("Only tracked entries can be merged", {
				duration: TOAST_DURATION.error,
			});
			return;
		}
		const sources = filteredEntries.filter((e) => checkedRows.has(e.id));
		setMergeEntries(sources as SharedEntry[]);
		setMergeConflicts(undefined);
	}, [selectedIds, selectionAllTracked, filteredEntries, checkedRows]);

	const handleMergeSubmit = useCallback(
		async (values: ActionFormSubmit) => {
			const sources = mergeEntries ?? [];
			const ids = sources.map((e) => e.id);
			if (ids.length === 0) return;

			setActionInProgress("merge");

			try {
				const resp = await makeReportingRequest({
					endpoint: "/v2/entries/merge",
					method: "POST",
					body: {
						sourceIds: ids.map(formatEntryId),
						matterId: values.matterId,
						taskId: values.taskId,
						activityId: values.activityId,
						date: formatDateForApi(values.date),
						unitsSeconds: values.unitsSeconds,
					},
				});
				if (resp.apiError) throw new ApiError(resp.apiError);
				if (resp.error) throw new Error(String(resp.error));

				setCheckedRows(new Set());
				setMergeEntries(null);
				setMergeConflicts(undefined);
				toast.success(`${ids.length} entries merged`);
				refresh();
			} catch (e) {
				// FIELD_CONFLICT keeps the dialog open with the conflicts overlaid so
				// the user can resolve them and resubmit. Any other error closes the
				// dialog and surfaces a toast.
				if (
					e instanceof ApiError &&
					e.code === "FIELD_CONFLICT" &&
					e.conflicts?.length
				) {
					setMergeConflicts(e.conflicts);
					return;
				}
				setMergeEntries(null);
				toast.error("Failed to merge entries", {
					duration: TOAST_DURATION.error,
					description: e instanceof Error ? e.message : undefined,
				});
			} finally {
				setActionInProgress(null);
			}
		},
		[mergeEntries, makeReportingRequest, refresh],
	);

	const handleGroup = useCallback(() => {
		if (selectedIds.length < 2) {
			toast.error("Select at least 2 entries to group", {
				duration: TOAST_DURATION.error,
			});
			return;
		}
		if (!selectionAllTracked) {
			toast.error("Only tracked entries can be grouped", {
				duration: TOAST_DURATION.error,
			});
			return;
		}
		const sources = filteredEntries.filter((e) => checkedRows.has(e.id));
		setGroupEntries(sources as SharedEntry[]);
		setGroupConflicts(undefined);
	}, [selectedIds, selectionAllTracked, filteredEntries, checkedRows]);

	const handleGroupSubmit = useCallback(
		async (values: ActionFormSubmit) => {
			const sources = groupEntries ?? [];
			const ids = sources.map((e) => e.id);
			if (ids.length === 0) return;
			setActionInProgress("group");
			try {
				const resp = await makeReportingRequest({
					endpoint: "/v2/entries/group",
					method: "POST",
					body: {
						sourceIds: ids.map(formatEntryId),
						matterId: values.matterId,
						taskId: values.taskId,
						activityId: values.activityId,
						date: formatDateForApi(values.date),
						unitsSeconds: values.unitsSeconds,
						...(values.includeUnits !== undefined
							? { includeUnits: values.includeUnits }
							: {}),
					},
				});
				if (resp.apiError) throw new ApiError(resp.apiError);
				if (resp.error) throw new Error(String(resp.error));
				setCheckedRows(new Set());
				setGroupEntries(null);
				setGroupConflicts(undefined);
				toast.success(`${ids.length} entries grouped`);
				refresh();
			} catch (e) {
				if (
					e instanceof ApiError &&
					e.code === "FIELD_CONFLICT" &&
					e.conflicts?.length
				) {
					setGroupConflicts(e.conflicts);
					return;
				}
				setGroupEntries(null);
				toast.error("Failed to group entries", {
					duration: TOAST_DURATION.error,
					description: e instanceof Error ? e.message : undefined,
				});
			} finally {
				setActionInProgress(null);
			}
		},
		[groupEntries, makeReportingRequest, refresh],
	);

	const handleCollapse = useCallback(() => {
		if (selectedIds.length < 2) {
			toast.error("Select at least 2 entries to collapse", {
				duration: TOAST_DURATION.error,
			});
			return;
		}
		if (!selectionAllTracked) {
			toast.error("Only tracked entries can be collapsed", {
				duration: TOAST_DURATION.error,
			});
			return;
		}
		const sources = filteredEntries.filter((e) => checkedRows.has(e.id));
		setCollapseEntries(sources as SharedEntry[]);
	}, [selectedIds, selectionAllTracked, filteredEntries, checkedRows]);

	const handleCollapseSubmit = useCallback(
		async (values: CollapseDialogSubmit) => {
			const sources = collapseEntries ?? [];
			const ids = sources.map((e) => e.id);
			if (ids.length === 0) return;
			setActionInProgress("collapse");
			try {
				const resp = await makeReportingRequest({
					endpoint: "/v2/entries/collapse",
					method: "POST",
					body: {
						sourceIds: ids.map(formatEntryId),
						mainSourceId: formatEntryId(values.mainSourceId),
						matterId: values.matterId,
						taskId: values.taskId,
						activityId: values.activityId,
						date: formatDateForApi(values.date),
						unitsSeconds: values.unitsSeconds,
					},
				});
				if (resp.apiError) throw new ApiError(resp.apiError);
				if (resp.error) throw new Error(String(resp.error));
				setCheckedRows(new Set());
				setCollapseEntries(null);
				toast.success(`${ids.length} entries collapsed`);
				refresh();
			} catch (e) {
				setCollapseEntries(null);
				toast.error("Failed to collapse entries", {
					duration: TOAST_DURATION.error,
					description: e instanceof Error ? e.message : undefined,
				});
			} finally {
				setActionInProgress(null);
			}
		},
		[collapseEntries, makeReportingRequest, refresh],
	);

	const handleAddEntry = useCallback(() => {
		if (!selectedProfId) {
			toast.error("Select an attorney before adding an entry");
			return;
		}
		setAddDialogOpen(true);
	}, [selectedProfId]);

	const handleSaveNewEntry = useCallback(
		async (
			newEntry: {
				id: number;
				date: string;
				matter: string;
				matter_id: string;
				description: string;
				duration: number;
				task: string;
				activity: string;
			},
			shouldExport: boolean,
		) => {
			try {
				const resp = await makeReportingRequest({
					endpoint: "/v2/entries",
					method: "POST",
					body: toCreateBody(newEntry),
				});
				if (resp.error) throw new Error(String(resp.error));

				// v1 export endpoints still take numeric ids — strip the "ent_" prefix.
				// Wrap so a malformed id (backend regression) surfaces as a partial-
				// success toast rather than the outer "Failed to add entry" — the
				// entry has already persisted and a retry would create a duplicate.
				let newEntryId: number | null = null;
				if (resp.data?.id) {
					try {
						newEntryId = parseEntryId(resp.data.id);
					} catch (err) {
						logger.captureException(
							err instanceof Error ? err : new Error(String(err)),
							{
								action: "internalAddEntry-parseEntryId",
								id: resp.data.id,
							},
						);
						if (shouldExport) {
							toast.error("Entry added but export failed", {
								duration: TOAST_DURATION.error,
								description: err instanceof Error ? err.message : undefined,
							});
						} else {
							toast.success("Entry added");
						}
						refresh();
						return;
					}
				}

				if (shouldExport && newEntryId) {
					const exportResp = await makeReportingRequest({
						endpoint: "/records/exportRecords",
						method: "POST",
						body: { ids: [newEntryId] },
					});
					if (exportResp.error) throw new Error(String(exportResp.error));
					toast.success("Entry added and exported");
				} else {
					toast.success("Entry added");
				}

				refresh();
			} catch (e) {
				if (e instanceof Error)
					logger.captureException(e, {
						action: "internalAddEntry",
						shouldExport,
					});
				toast.error("Failed to add entry", {
					duration: TOAST_DURATION.error,
					description: e instanceof Error ? e.message : undefined,
				});
			}
		},
		[makeReportingRequest, refresh],
	);

	const handleSaveEditEntry = useCallback(
		async (
			updatedEntry: {
				id: number;
				date: string;
				matter: string;
				matter_id: string;
				description: string;
				duration: number;
				task: string;
				activity: string;
			},
			shouldExport: boolean,
		) => {
			setMutatingIds((prev) => {
				const next = new Set(prev);
				next.add(updatedEntry.id);
				return next;
			});
			setShowEditModal(false);

			try {
				const resp = await makeReportingRequest({
					endpoint: `/v2/entries/${formatEntryId(updatedEntry.id)}`,
					method: "PATCH",
					body: toUpdateBody(updatedEntry),
				});
				if (resp.error) throw new Error(String(resp.error));

				if (shouldExport) {
					const exportResp = await makeReportingRequest({
						endpoint: "/records/exportRecords",
						method: "POST",
						body: { ids: [updatedEntry.id] },
					});
					if (exportResp.error) throw new Error(String(exportResp.error));
					toast.success("Entry saved and exported");
				} else {
					toast.success("Entry saved");
				}

				refresh();
			} catch (e) {
				refresh();
				if (e instanceof Error)
					logger.captureException(e, {
						action: "internalEditEntry",
						entryId: updatedEntry.id,
						shouldExport,
					});
				toast.error("Failed to save entry", {
					duration: TOAST_DURATION.error,
					description: e instanceof Error ? e.message : undefined,
				});
			} finally {
				setMutatingIds((prev) => {
					const next = new Set(prev);
					next.delete(updatedEntry.id);
					return next;
				});
			}
		},
		[makeReportingRequest, refresh],
	);

	const handleEditDuplicate = useCallback(
		async (
			entry: {
				date: string;
				matter: string;
				matter_id: string;
				description: string;
				duration: number;
				task: string;
				activity: string;
				document_name: string | null;
				client: string | null;
				units_seconds: number;
				start_time: string | null;
				end_time: string | null;
			},
			originalId: number,
		) => {
			setMutatingIds((prev) => {
				const next = new Set(prev);
				next.add(originalId);
				return next;
			});
			try {
				const resp = await makeReportingRequest({
					endpoint: "/records/duplicate",
					method: "POST",
					body: { entry, duplicated_from_id: originalId },
				});
				if (resp.error) throw new Error(String(resp.error));
				toast.success("Entry duplicated");
				refresh();
			} catch {
				toast.error("Failed to duplicate entry", {
					duration: TOAST_DURATION.error,
				});
			} finally {
				setMutatingIds((prev) => {
					const next = new Set(prev);
					next.delete(originalId);
					return next;
				});
			}
		},
		[makeReportingRequest, refresh],
	);

	// Resolve attorney feedback
	const handleResolveFeedback = useCallback(
		async (entryId: number) => {
			try {
				const resp = await makeReportingRequest({
					endpoint: `/records/${entryId}/resolve-feedback`,
					method: "POST",
				});
				if (resp.error) throw new Error(String(resp.error));
				refresh();
				setSelectedRowData((prev) =>
					prev ? { ...prev, attorney_feedback: null } : null,
				);
				toast.success("Feedback resolved");
			} catch (e) {
				toast.error("Failed to resolve feedback", {
					duration: TOAST_DURATION.error,
				});
			}
		},
		[makeReportingRequest, refresh],
	);

	const handleRowClick = (entry: Entry) => {
		if (entry.status !== "tracked") return;
		const cached = sourceEntries.find((e) => e.id === entry.id);
		setSelectedRowData({
			id: entry.id,
			date: entry.date,
			matter_name: entry.matter,
			matter_id: entry.matter_id || "",
			description: entry.description,
			duration: entry.duration,
			task: entry.task,
			activity: entry.activity || "",
			document_name: entry.document_name || "",
			original_description: cached?.original_description ?? null,
			original_units: cached?.original_units ?? null,
			original_matter: cached?.original_matter ?? null,
			original_task: cached?.original_task ?? null,
			original_activity: cached?.original_activity ?? null,
			attorney_feedback: cached?.attorney_feedback ?? null,
		});
		setShowEditModal(true);
		setSelectedExportError(failedExportEntries.get(entry.id) ?? null);
	};

	// Calendar helpers
	const dayKey = (d: Date) =>
		`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

	const dailySums = useMemo(() => {
		const sums: Record<
			string,
			{ unexported: number; exported: number; deleted: number }
		> = {};
		const accumulate = (
			tx: ReportingEntry,
			bucket: "unexported" | "exported" | "deleted",
		) => {
			const datePart = tx.date.split("T")[0];
			const [year, month, day] = datePart.split("-").map(Number);
			const k = dayKey(new Date(year, month - 1, day));
			sums[k] = sums[k] || { unexported: 0, exported: 0, deleted: 0 };
			sums[k][bucket] += tx.units;
		};
		for (const tx of cachedTracked) accumulate(tx, "unexported");
		for (const tx of cachedExported) accumulate(tx, "exported");
		for (const tx of cachedDeleted) accumulate(tx, "deleted");
		return sums;
	}, [cachedTracked, cachedExported, cachedDeleted]);

	const calendarDays = useMemo(() => {
		const firstOfMonth = new Date(
			calendarMonthDate.getFullYear(),
			calendarMonthDate.getMonth(),
			1,
		);
		const startDay = new Date(firstOfMonth);
		startDay.setDate(startDay.getDate() - startDay.getDay());
		const days: Date[] = [];
		for (let i = 0; i < 42; i++) {
			const d = new Date(startDay);
			d.setDate(startDay.getDate() + i);
			days.push(d);
		}
		return days;
	}, [calendarMonthDate]);

	const isSameDay = (a: Date, b: Date) =>
		a.getFullYear() === b.getFullYear() &&
		a.getMonth() === b.getMonth() &&
		a.getDate() === b.getDate();

	const emptyMessage = useMemo(() => {
		if (!selectedProfId)
			return "Select an attorney to view their time entries.";
		if (filteredEntries.length > 0) return undefined;
		if (entriesError)
			return `Failed to load entries${entriesErrorMessage ? `: ${entriesErrorMessage}` : "."}`;
		if (tabFilteredEntries.length > 0)
			return "No entries match the current filter.";
		return "No entries for this date.";
	}, [
		selectedProfId,
		filteredEntries.length,
		tabFilteredEntries.length,
		entriesError,
		entriesErrorMessage,
	]);

	if (profLoading) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-muted-foreground">Loading attorneys...</p>
			</div>
		);
	}

	const headerViewMode = visibleStatuses.has("tracked")
		? ("tracked" as const)
		: ("history" as const);

	const viewToggle = (
		<div className="inline-flex items-center rounded-md border border-border bg-card p-0.5">
			{(["list", "calendar"] as const).map((mode) => {
				const Icon = mode === "list" ? List : Calendar;
				const isActive = viewMode === mode;
				return (
					<Button
						key={mode}
						type="button"
						variant="ghost"
						size="sm"
						aria-pressed={isActive}
						onClick={() => setViewMode(mode)}
						className={cn(
							"h-6 sm:h-7 gap-1.5 px-2 text-[11px] sm:text-xs font-medium capitalize",
							isActive ? "bg-muted text-foreground" : "text-muted-foreground",
						)}
					>
						<Icon className="size-3.5" />
						{mode}
					</Button>
				);
			})}
		</div>
	);

	return (
		<div className="flex flex-col h-full bg-background">
			<div className="flex items-center gap-3 px-5 py-3 border-b border-border">
				<AttorneySelector
					professionals={allProfessionals}
					selectedProfId={selectedProfId}
					onSelect={setSelectedProfId}
				/>

				<div className="ml-auto flex items-center gap-3">
					{viewMode === "list" && (
						<StatusFilterChips
							active={visibleStatuses}
							onToggle={toggleStatus}
						/>
					)}
					{viewToggle}
				</div>
			</div>

			{viewMode === "list" && (
				<div className="px-3">
					<HeaderBar
						viewMode={headerViewMode}
						date={selectedDate}
						onDateChange={setSelectedDate}
						dateRange={dateRange}
						onDateRangeChange={setDateRange}
						showTimer={false}
						timerRunning={false}
						elapsedSeconds={0}
						onStart={() => {}}
						onStop={() => {}}
						onReset={() => {}}
						selectionCount={selectionCount}
						onAddEntry={handleAddEntry}
						onMerge={handleMerge}
						onGroup={handleGroup}
						onCollapse={handleCollapse}
						onDuplicate={handleDuplicate}
						onDelete={handleDelete}
						onExport={handleExport}
						isExporting={isExporting}
						actionInProgress={actionInProgress}
						isApplicationVisible={isApplicationVisible}
						onToggleApplication={toggleApplication}
						onShowAllApplications={showAllApplications}
						onHideAllApplications={hideAllApplications}
						clientMatter={{
							clients: availableClients,
							matters: availableMatters,
							clientFilter,
							matterFilter,
							onClientChange: (client) => setParams({ client, matter: null }),
							onMatterChange: (matterId, client) =>
								matterId === null
									? setParams({ matter: null })
									: setParams({ matter: matterId, client }),
						}}
					/>
				</div>
			)}

			{/* Main content */}
			<div className="flex-1 flex flex-col overflow-hidden">
				{viewMode === "calendar" ? (
					<div className="border border-border rounded-lg overflow-hidden flex-1 flex flex-col mx-3 mb-3 mt-4">
						{!selectedProfId ? (
							<div className="flex-1 flex items-center justify-center text-muted-foreground">
								Select an attorney to view their calendar
							</div>
						) : entriesLoading ? (
							<div className="flex-1 flex items-center justify-center text-muted-foreground">
								Loading...
							</div>
						) : (
							<div className={calendarStyles.calendarView}>
								<div className={calendarStyles.calendarHeader}>
									<span className={calendarStyles.calendarHeaderLabel}>
										{calendarMonthDate.toLocaleDateString(undefined, {
											month: "long",
											year: "numeric",
										})}
									</span>
									<div className={calendarStyles.calendarHeaderButtonWrapper}>
										<button
											className={calendarStyles.calendarPageButton}
											onClick={() => {
												const d = new Date(calendarMonthDate);
												d.setMonth(d.getMonth() - 1);
												setCalendarMonthDate(d);
											}}
										>
											<img
												src={
													darkMode ? navArrowLeftWhiteIcon : navArrowLeftIcon
												}
												alt="Previous"
											/>
										</button>
										<button
											className={`${calendarStyles.calendarPageButton} ${calendarStyles.calendarTodayButton}`}
											onClick={() => setCalendarMonthDate(new Date())}
										>
											Today
										</button>
										<button
											className={calendarStyles.calendarPageButton}
											onClick={() => {
												const d = new Date(calendarMonthDate);
												d.setMonth(d.getMonth() + 1);
												setCalendarMonthDate(d);
											}}
										>
											<img
												src={
													darkMode ? navArrowRightWhiteIcon : navArrowRightIcon
												}
												alt="Next"
											/>
										</button>
									</div>
								</div>
								<div className={calendarStyles.calendarGrid}>
									{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
										(day) => (
											<div
												key={day}
												className={calendarStyles.calendarWeekdayHeader}
											>
												{day}
											</div>
										),
									)}
									{calendarDays.map((currDay, index) => {
										const isCurrentMonth =
											currDay.getMonth() === calendarMonthDate.getMonth();
										const isWeekend =
											currDay.getDay() === 0 || currDay.getDay() === 6;
										const key = dayKey(currDay);
										const sums = dailySums[key];
										const unexported = (sums?.unexported || 0).toFixed(1);
										const exported = (sums?.exported || 0).toFixed(1);
										const deleted = (sums?.deleted || 0).toFixed(1);
										return (
											<button
												key={index}
												onClick={() => {
													if (selectedProfId) {
														setSelectedDate(currDay);
														setViewMode("list");
													}
												}}
											>
												<div
													className={`${calendarStyles.calendarCard} ${!isCurrentMonth ? calendarStyles.calendarOutsideMonthCard : ""} ${isWeekend ? calendarStyles.calendarWeekendCard : ""}`}
													data-today={isSameDay(currDay, new Date())}
												>
													<div className={calendarStyles.cardDate}>
														{currDay.getDate()}
													</div>
													<div
														className={`${calendarStyles.cardMetric} ${calendarStyles.cardMetricUnits}`}
													>
														{unexported} hrs to export
													</div>
													<div
														className={`${calendarStyles.cardMetric} ${calendarStyles.cardMetricUnits}`}
													>
														{exported} hrs exported
													</div>
													{sums && sums.deleted > 0 && (
														<div
															className={`${calendarStyles.cardMetric} ${calendarStyles.cardMetricUnits} text-red-600 dark:text-red-400`}
														>
															{deleted} hrs deleted
														</div>
													)}
												</div>
											</button>
										);
									})}
								</div>
							</div>
						)}
					</div>
				) : (
					<div className="border border-border rounded-lg overflow-hidden flex-1 flex flex-col mx-3 mb-3">
						{entriesLoading ? (
							<div className="flex-1 flex items-center justify-center text-muted-foreground">
								Loading...
							</div>
						) : (
							<InternalEntriesTable
								key={
									dateRange
										? `${dateRange.from?.toDateString()}-${dateRange.to?.toDateString()}`
										: selectedDate.toDateString()
								}
								entries={filteredEntries}
								matters={matters}
								checkedRows={checkedRows}
								setCheckedRows={setCheckedRows}
								isLoading={entriesLoading}
								onRowClick={selectedProfId ? handleRowClick : undefined}
								sortingMode={sortingMode}
								sortDirection={sortDirection}
								emptyMessage={emptyMessage}
							/>
						)}
					</div>
				)}
			</div>

			{/* Add Entry Dialog */}
			<InternalAddEntryDialog
				open={addDialogOpen}
				onOpenChange={setAddDialogOpen}
				onSave={handleSaveNewEntry}
				matters={matters}
				tasks={tasks}
				activities={activities}
				currentDate={selectedDate}
				abbreviations={abbreviationProps}
			/>

			{selectedRowData && selectedProfId && (
				<InternalEditEntry
					onSave={handleSaveEditEntry}
					onDuplicate={handleEditDuplicate}
					onResolveFeedback={() => handleResolveFeedback(selectedRowData.id)}
					attorneyFeedback={selectedRowData.attorney_feedback ?? null}
					matters={matters}
					tasks={tasks}
					activities={activities}
					selectedRowData={selectedRowData}
					open={showEditModal}
					onOpenChange={setShowEditModal}
					abbreviations={abbreviationProps}
					exportError={selectedExportError}
				/>
			)}

			<MergeDialog
				entries={mergeEntries ?? []}
				open={mergeEntries !== null}
				onOpenChange={(open) => {
					if (!open) {
						setMergeEntries(null);
						setMergeConflicts(undefined);
					}
				}}
				conflicts={mergeConflicts}
				onSubmit={handleMergeSubmit}
			/>

			<GroupDialog
				entries={groupEntries ?? []}
				open={groupEntries !== null}
				onOpenChange={(open) => {
					if (!open) {
						setGroupEntries(null);
						setGroupConflicts(undefined);
					}
				}}
				conflicts={groupConflicts}
				onSubmit={handleGroupSubmit}
			/>

			<CollapseDialog
				entries={collapseEntries ?? []}
				open={collapseEntries !== null}
				onOpenChange={(open) => {
					if (!open) setCollapseEntries(null);
				}}
				onSubmit={handleCollapseSubmit}
			/>
		</div>
	);
}
